import * as React from "react";
import { styled, Theme, CSSObject, useTheme } from "@mui/material/styles";
import { Drawer as MuiDrawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Avatar, Typography, Divider, Tooltip, Zoom, MenuItem } from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { IconButton, Menu, TooltipShortcut } from "@itk/mui-components";
import { useEffect, useRef, useState } from "react";

import sidebarBackground from "src/assets/images/sidebar-background.svg";
import dekoLogoAndText from "src/assets/images/deko-logo-and-text.svg";
import { CONSTANTS, EXTERNAL_LINKS, ROUTES_URL } from "src/utils/constants";
import { connect } from "react-redux";
import { RootState } from "src/store";
import { applicationActions } from "../../../application-store";
import { usePriceListPageT } from "src/translation-hooks/price-list-page";
import { useProjectsPageT } from "src/translation-hooks/projects-page";
import { useContactPageT } from "src/translation-hooks/contact-page";
import { useFaqPageT } from "src/translation-hooks/faq-page";
import { useInformationPageT } from "src/translation-hooks/information-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useGetContactPersonDetail, useGetCustomerDetails, usePermissionCheck, useUserSettings } from "src/hooks";
import { Model_CustomerPortalPossibleCustomerGet, Shared_ContactPersonPermissionKinds } from "src/backend/openapi/v1";
import { SidebarMenuItem } from "./sidebar-types";
import { DekoxIcon } from "src/components/deko-x-icons";
import { isOverflowing } from "src/utils";
import { informationTabs } from "src/pages/information/information-types";

const commonStyles = (theme: Theme): CSSObject => ({
  backgroundImage: `url(${sidebarBackground})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundPosition: "bottom",
  color: theme.palette.customColors.white,
  overflowX: "hidden"
});

const openedMixin = (theme: Theme): CSSObject => ({
  width: CONSTANTS.DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  ...commonStyles(theme)
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: `calc(${theme.spacing(7)} + 1px)`,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...commonStyles(theme)
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: prop => prop !== "open" })(({ theme }) => ({
  width: CONSTANTS.DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme)
      }
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme)
      }
    }
  ]
}));

const ownActions = {
  setIsSidebarOpen: applicationActions.setIsSidebarOpen
};

const mapStateToProps = (state: RootState) => ({
  isSidebarOpen: state.generalReducer.applicationReducer.isSidebarOpen,
  customers: state.generalReducer.applicationReducer.customers,
  currentCustomer: state.generalReducer.applicationReducer.currentCustomer
});

type Props = ReturnType<typeof mapStateToProps> & typeof ownActions;

const SidebarBase = ({ isSidebarOpen, setIsSidebarOpen, customers, currentCustomer }: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const ref = useRef<HTMLAnchorElement>(null);

  const genericTranslationsT = useGenericTranslationsT();
  const priceListPageT = usePriceListPageT();
  const projectPageT = useProjectsPageT();
  const contactPageT = useContactPageT();
  const informationPageT = useInformationPageT();
  const faqPageT = useFaqPageT();
  const location = useLocation();
  const { customerDetails } = useGetCustomerDetails();
  const [customerMenuAnchor, setCustomerMenuAnchor] = React.useState<null | HTMLElement>(null);
  const { language } = useUserSettings();
  const [overflowing, setOverflowing] = useState(false);
  const { contactPersonDetail } = useGetContactPersonDetail();

  const [isAllowedToViewPricelist, isAllowedToViewProjects, isAllowedToShowDekoSupportLink, isAllowedToShowPictureStoreLink] = usePermissionCheck([
    Shared_ContactPersonPermissionKinds.ViewPricelist,
    Shared_ContactPersonPermissionKinds.ViewProjects,
    Shared_ContactPersonPermissionKinds.ShowDekoSupportLink,
    Shared_ContactPersonPermissionKinds.ShowPictureStoreLink
  ]);

  useEffect(() => {
    if (ref.current) {
      setOverflowing(isOverflowing(ref.current));
    }
  }, [customerDetails?.name, contactPersonDetail?.fullName]);

  const handleCustomerChange = (value: Model_CustomerPortalPossibleCustomerGet | null) => {
    if (!value) return;

    localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.CUSTOMER_ID, value.customerId);
    window.location.href = "/";
  };

  // List of items to show on sidebar
  const sidebarItems = React.useMemo((): SidebarMenuItem[] => {
    return [
      {
        label: priceListPageT.pricelist,
        icon: <DekoxIcon icon="localOffer" />,
        isActive: location.pathname.includes(ROUTES_URL.PRICE_LIST),
        onClick: () => {
          navigate(ROUTES_URL.PRICE_LIST);
        },
        visible: isAllowedToViewPricelist,
        shortcutKey: "1"
      },
      {
        label: projectPageT.projects,
        icon: <DekoxIcon icon="handyman" />,
        isActive: location.pathname.includes(ROUTES_URL.PROJECTS),
        onClick: () => {
          navigate(ROUTES_URL.PROJECTS);
        },
        visible: isAllowedToViewProjects,
        shortcutKey: "2"
      },
      {
        label: contactPageT.contact,
        icon: <DekoxIcon icon="callOutline" />,
        isActive: location.pathname.includes(ROUTES_URL.CONTACT),
        onClick: () => {
          navigate(ROUTES_URL.CONTACT);
        },
        visible: true,
        shortcutKey: "3"
      },
      {
        label: faqPageT.faqAbbreviation,
        icon: <DekoxIcon icon="help" />,
        isActive: location.pathname.includes(ROUTES_URL.FAQ),
        onClick: () => {
          navigate(ROUTES_URL.FAQ);
        },
        visible: true,
        shortcutKey: "4"
      },
      {
        label: genericTranslationsT.dekoSupport,
        icon: <DekoxIcon icon="supportAgent" />,
        isActive: false,
        onClick: () => {
          window.open(EXTERNAL_LINKS.DEKO_SUPPORT, "_blank", "noopener,noreferrer");
        },
        visible: isAllowedToShowDekoSupportLink
      },
      {
        label: genericTranslationsT.imagebank,
        icon: <DekoxIcon icon="photo" />,
        isActive: false,
        onClick: () => (language === "en" ? window.open(EXTERNAL_LINKS.IMAGE_BANK_ENGLISH) : window.open(EXTERNAL_LINKS.IMAGE_BANK_DANISH)),
        visible: isAllowedToShowPictureStoreLink
      }
    ].filter(x => x.visible);
  }, [
    genericTranslationsT,
    priceListPageT,
    projectPageT,
    informationPageT,
    contactPageT,
    faqPageT,
    location.pathname,
    isAllowedToViewPricelist,
    isAllowedToViewProjects,
    isAllowedToShowDekoSupportLink,
    isAllowedToShowPictureStoreLink,
    language
  ]);

  return (
    <Drawer variant="permanent" open={isSidebarOpen}>
      <Stack padding={1} paddingTop={2}>
        <Stack direction="row" justifyContent={isSidebarOpen ? "space-between" : "center"} flex={1} alignItems="center" ml={isSidebarOpen ? 2 : 0}>
          {isSidebarOpen && <img src={dekoLogoAndText} alt="Deko logo" style={{ cursor: "pointer" }} onClick={() => navigate(ROUTES_URL.HOME)} />}
          <IconButton sx={{ color: "inherit" }} k={genericTranslationsK.menu} onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            {isSidebarOpen ? <DekoxIcon icon="doubleArrowleft" /> : <DekoxIcon icon="doubleArrowRight" />}
          </IconButton>
        </Stack>
      </Stack>
      <Stack justifyContent="space-between" flex={1}>
        <List>
          {sidebarItems.map(item => {
            return (
              <ListItem key={item.label} disablePadding sx={{ display: "block", backgroundColor: item.isActive ? theme.palette.primary.main : "inherit" }} onClick={item.onClick}>
                <Tooltip title={isSidebarOpen ? "" : item.label} arrow placement="right" slots={{ transition: Zoom }}>
                  {item.shortcutKey ? (
                    <TooltipShortcut shortcut={{ action: item.onClick, shortCutKey: item.shortcutKey, modifier: "alt" }}>
                      <ListItemButton sx={[{ minHeight: 48, px: 2.5, justifyContent: isSidebarOpen ? "initial" : "center" }]}>
                        <ListItemIcon sx={[{ minWidth: 0, justifyContent: "center", color: "inherit", mr: isSidebarOpen ? 2 : "auto" }]}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} sx={{ fontSize: theme.typography.body1.fontSize, fontWeight: "normal", opacity: isSidebarOpen ? 1 : 0 }} />
                      </ListItemButton>
                    </TooltipShortcut>
                  ) : (
                    <ListItemButton sx={[{ minHeight: 48, px: 2.5, justifyContent: isSidebarOpen ? "initial" : "center" }]}>
                      <ListItemIcon sx={[{ minWidth: 0, justifyContent: "center", color: "inherit", mr: isSidebarOpen ? 2 : "auto" }]}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} sx={{ fontSize: theme.typography.body1.fontSize, fontWeight: "normal", opacity: isSidebarOpen ? 1 : 0 }} />
                    </ListItemButton>
                  )}
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
        <List disablePadding>
          <Divider />
          <ListItem
            disablePadding
            sx={{ display: "block", backgroundColor: location.pathname.includes(ROUTES_URL.INFORMATION.pattern) ? theme.palette.primary.main : "inherit" }}
            onClick={() => {
              navigate(ROUTES_URL.INFORMATION.build(informationTabs[0]));
            }}
          >
            <Tooltip title={isSidebarOpen ? "" : `${customerDetails?.name} - ${customerDetails?.customerNumber}`} arrow placement="right" slots={{ transition: Zoom }}>
              <ListItemButton
                sx={[
                  {
                    minHeight: 48,
                    px: 2.5,
                    justifyContent: isSidebarOpen ? "initial" : "center"
                  }
                ]}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: "center",
                      color: "inherit",
                      mr: isSidebarOpen ? 2 : "auto"
                    }
                  ]}
                >
                  <DekoxIcon icon="apartment" />
                </ListItemIcon>
                {isSidebarOpen && customerDetails && (
                  <Stack direction="column" flex={1}>
                    <Typography>{customerDetails?.customerNumber}</Typography>

                    <Tooltip title={overflowing ? customerDetails.name : ""} arrow disableHoverListener={!overflowing}>
                      <Typography
                        variant="subtitle1"
                        ref={ref}
                        noWrap
                        sx={{
                          textDecoration: customers.loading === "has-loaded" && customers.data.length > 1 ? "underline" : "none",
                          display: "inline-block",
                          textOverflow: "ellipsis",
                          maxWidth: 160
                        }}
                        onClick={e => {
                          if (customers.loading === "has-loaded" && customers.data.length > 1) {
                            e.stopPropagation();
                            setCustomerMenuAnchor(e.currentTarget);
                          }
                        }}
                      >
                        {customerDetails?.name}
                      </Typography>
                    </Tooltip>
                  </Stack>
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
          <Divider />
          <ListItem
            disablePadding
            sx={{
              backgroundColor: location.pathname.includes(ROUTES_URL.PROFILE) ? theme.palette.primary.main : "inherit",
              cursor: "pointer"
            }}
            onClick={() => {
              navigate(ROUTES_URL.PROFILE);
            }}
          >
            <Stack px={2} direction="row" width="100%" alignItems="center" justifyContent={isSidebarOpen ? "flex-start" : "center"} gap={1}>
              <Avatar
                src={contactPersonDetail?.profilePicture ? contactPersonDetail.profilePicture : undefined}
                sx={{
                  width: 40,
                  height: 40,
                  marginY: 2,
                  backgroundColor: theme.palette.primary.dark
                }}
              >
                {contactPersonDetail?.fullName.charAt(0)}
              </Avatar>

              {isSidebarOpen && (
                <Tooltip title={overflowing ? contactPersonDetail?.fullName : ""} arrow placement="bottom" disableHoverListener={!overflowing}>
                  <Typography
                    ref={ref}
                    variant="h4"
                    sx={{
                      textDecoration: customers.loading === "has-loaded" && customers.data.length > 1 ? "underline" : "none",
                      maxWidth: 135,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      lineHeight: 1.2
                    }}
                  >
                    {contactPersonDetail?.fullName}
                  </Typography>
                </Tooltip>
              )}
            </Stack>
          </ListItem>
        </List>
      </Stack>
      {customers.loading === "has-loaded" && customers.data.length > 1 && (
        <Menu
          anchorEl={customerMenuAnchor}
          open={!!customerMenuAnchor}
          onClose={() => setCustomerMenuAnchor(null)}
          transformOrigin={{ horizontal: "left", vertical: "bottom" }}
          anchorOrigin={{ horizontal: "left", vertical: "top" }}
        >
          {customers.data.map(customer => {
            const isCurrentCustomer = currentCustomer.loading === "has-loaded" && currentCustomer.data.customerId === customer.customerId;
            return (
              <MenuItem key={customer.customerId} selected={isCurrentCustomer} onClick={() => !isCurrentCustomer && handleCustomerChange(customer)}>
                <Typography variant="body1">{customer.customerName}</Typography>
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </Drawer>
  );
};

export const Sidebar = connect(mapStateToProps, ownActions)(SidebarBase);
